import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  createTemplate,
  getTemplate,
  updateTemplate,
} from "../../../helpers/templates_helper";
import CampaignEasyEmailEditor from "../Campaigns/CampaignEasyEmailEditor";

type FormValues = {
  name: string;
  html_body: string;
  editor_design: Record<string, unknown> | null;
};

type ContentTab = "editor" | "source" | "preview";

const AUTOSAVE_MS = 1500;

type DraftPayload = FormValues;

const draftStorageKey = (templateId: string | undefined): string =>
  templateId
    ? `brenvio.template.draft.${templateId}`
    : "brenvio.template.draft.new";

const readDraft = (key: string): DraftPayload | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as DraftPayload;
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    return {
      name: String(parsed.name || ""),
      html_body: String(parsed.html_body || ""),
      editor_design:
        parsed.editor_design && typeof parsed.editor_design === "object"
          ? parsed.editor_design
          : null,
    };
  } catch {
    return null;
  }
};

const writeDraft = (key: string, values: FormValues): void => {
  const hasContent = values.name.trim() || values.html_body.trim();
  if (!hasContent) {
    localStorage.removeItem(key);
    return;
  }
  localStorage.setItem(key, JSON.stringify(values));
};

const clearDraft = (key: string): void => {
  localStorage.removeItem(key);
};

/**
 * Template do tenant — Easy Email + código + preview + rascunho local (DOC 03 § 5.2 · 13).
 */
const TemplateForm = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const draftKey = draftStorageKey(id);

  document.title = isEdit
    ? t("templates.form.documentTitleEdit")
    : t("templates.form.documentTitleNew");

  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ContentTab>("editor");
  const [draftSavedAt, setDraftSavedAt] = useState<Date | null>(null);
  const [draftPrompt, setDraftPrompt] = useState<DraftPayload | null>(null);
  const [serverBaseline, setServerBaseline] = useState<FormValues>({
    name: "",
    html_body: "",
    editor_design: null,
  });
  const [initialValues, setInitialValues] = useState<FormValues>({
    name: "",
    html_body: "",
    editor_design: null,
  });
  const [editorRemountKey, setEditorRemountKey] = useState("boot");

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string()
          .trim()
          .max(255, t("templates.form.validation.nameMax"))
          .required(t("templates.form.validation.nameRequired")),
        html_body: Yup.string().required(
          t("templates.form.validation.htmlRequired")
        ),
      }),
    [t, i18n.language]
  );

  const validation = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSaving(true);
      setFormError(null);
      const payload = {
        name: values.name.trim(),
        html_body: values.html_body,
        editor_design: values.editor_design,
      };
      try {
        if (isEdit && id) {
          const res = await updateTemplate(Number(id), payload);
          toast.success(res.message || t("templates.toast.updated"), {
            autoClose: 2500,
          });
        } else {
          const res = await createTemplate(payload);
          toast.success(res.message || t("templates.toast.created"), {
            autoClose: 2500,
          });
        }
        clearDraft(draftKey);
        navigate("/templates");
      } catch (err: any) {
        const details = err?.details;
        if (details && typeof details === "object") {
          const mapped: Record<string, string> = {};
          Object.keys(details).forEach((key) => {
            mapped[key] = Array.isArray(details[key])
              ? details[key][0]
              : String(details[key]);
          });
          validation.setErrors(mapped);
        }
        setFormError(err?.message || t("templates.error.save"));
      } finally {
        setSaving(false);
      }
    },
  });

  useEffect(() => {
    if (!isEdit || !id) {
      const existing = readDraft(draftKey);
      if (existing && (existing.name || existing.html_body)) {
        setDraftPrompt(existing);
      }
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await getTemplate(Number(id));
        if (cancelled) {
          return;
        }
        if (!res?.success || !res.data) {
          setLoadError(t("templates.error.loadOne"));
          return;
        }
        const fromServer: FormValues = {
          name: res.data.name || "",
          html_body: res.data.html_body || "",
          editor_design: res.data.editor_design || null,
        };
        setServerBaseline(fromServer);
        setInitialValues(fromServer);
        setEditorRemountKey(`server-${res.data.id}-${Date.now()}`);

        const existing = readDraft(draftKey);
        if (
          existing &&
          (existing.name !== fromServer.name ||
            existing.html_body !== fromServer.html_body)
        ) {
          setDraftPrompt(existing);
        }
      } catch (err: any) {
        if (!cancelled) {
          setLoadError(err?.message || t("templates.error.loadOne"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, t, draftKey]);

  useEffect(() => {
    if (loading || loadError || draftPrompt) {
      return;
    }
    const timer = window.setTimeout(() => {
      writeDraft(draftKey, validation.values);
      const hasContent =
        validation.values.name.trim() || validation.values.html_body.trim();
      if (hasContent) {
        setDraftSavedAt(new Date());
      }
    }, AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
  }, [validation.values, draftKey, loading, loadError, draftPrompt]);

  const applyDraft = () => {
    if (!draftPrompt) {
      return;
    }
    setInitialValues(draftPrompt);
    setEditorRemountKey(`draft-${Date.now()}`);
    setDraftPrompt(null);
    toast.info(t("templates.form.draft.restored"), { autoClose: 2500 });
  };

  const discardDraft = () => {
    clearDraft(draftKey);
    setDraftPrompt(null);
    if (isEdit) {
      setInitialValues(serverBaseline);
      setEditorRemountKey(`discard-${Date.now()}`);
    }
  };

  const onEditorChange = useCallback(
    (payload: {
      editor_design: Record<string, unknown>;
      html_body: string;
    }) => {
      validation.setFieldValue("editor_design", payload.editor_design);
      validation.setFieldValue("html_body", payload.html_body);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validation.setFieldValue]
  );

  const onSourceChange = (html: string) => {
    validation.setFieldValue("html_body", html);
    validation.setFieldValue("editor_design", null);
    setEditorRemountKey(`source-${Date.now()}`);
  };

  const draftLabel = (): string | null => {
    if (!draftSavedAt) {
      return null;
    }
    return t("templates.form.draft.saved", {
      time: draftSavedAt.toLocaleTimeString(),
    });
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("templates.form.titleEdit")}
            pageTitle={t("templates.breadcrumb")}
          />
          <Alert color="danger">{loadError}</Alert>
          <Button color="light" tag={Link} to="/templates">
            {t("templates.form.backToList")}
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={
            isEdit
              ? t("templates.form.titleEdit")
              : t("templates.form.titleNew")
          }
          pageTitle={t("templates.breadcrumb")}
        />

        <ToastContainer closeButton={false} limit={1} />

        {formError && (
          <Alert color="danger" toggle={() => setFormError(null)}>
            {formError}
          </Alert>
        )}

        {draftPrompt && (
          <Alert color="info" className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
            <span>{t("templates.form.draft.found")}</span>
            <span className="d-flex gap-2">
              <Button color="primary" size="sm" onClick={applyDraft}>
                {t("templates.form.draft.restore")}
              </Button>
              <Button color="light" size="sm" onClick={discardDraft}>
                {t("templates.form.draft.discard")}
              </Button>
            </span>
          </Alert>
        )}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <h5 className="card-title mb-0">
                    {isEdit
                      ? t("templates.form.titleEdit")
                      : t("templates.form.titleNew")}
                  </h5>
                  {draftLabel() && (
                    <Badge color="light" className="text-muted">
                      {draftLabel()}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardBody>
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                  }}
                >
                  <div className="mb-3">
                    <Label htmlFor="template-name">
                      {t("templates.form.name")}
                    </Label>
                    <Input
                      id="template-name"
                      name="name"
                      value={validation.values.name}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        !!(validation.touched.name && validation.errors.name)
                      }
                      placeholder={t("templates.form.namePlaceholder")}
                    />
                    {validation.touched.name && validation.errors.name && (
                      <FormFeedback type="invalid">
                        {validation.errors.name}
                      </FormFeedback>
                    )}
                    <div className="form-text">{t("templates.form.nameHelp")}</div>
                  </div>

                  <Nav tabs className="nav-tabs-custom mb-3">
                    {(
                      [
                        ["editor", "templates.form.tab.editor"],
                        ["source", "templates.form.tab.source"],
                        ["preview", "templates.form.tab.preview"],
                      ] as const
                    ).map(([tab, labelKey]) => (
                      <NavItem key={tab}>
                        <NavLink
                          className={classnames({ active: activeTab === tab })}
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setActiveTab(tab);
                          }}
                        >
                          {t(labelKey)}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>

                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="editor">
                      {activeTab === "editor" && (
                        <CampaignEasyEmailEditor
                          design={validation.values.editor_design}
                          subject={validation.values.name}
                          remountKey={editorRemountKey}
                          onChange={onEditorChange}
                        />
                      )}
                      {validation.touched.html_body &&
                        validation.errors.html_body && (
                          <div className="text-danger small mt-2">
                            {validation.errors.html_body}
                          </div>
                        )}
                    </TabPane>
                    <TabPane tabId="source">
                      <Label htmlFor="template-html">
                        {t("templates.form.htmlBody")}
                      </Label>
                      <Input
                        id="template-html"
                        type="textarea"
                        rows={16}
                        className="font-monospace"
                        value={validation.values.html_body}
                        onChange={(e) => onSourceChange(e.target.value)}
                        onBlur={validation.handleBlur}
                        name="html_body"
                        invalid={
                          !!(
                            validation.touched.html_body &&
                            validation.errors.html_body
                          )
                        }
                        placeholder={t("templates.form.htmlPlaceholder")}
                      />
                      {validation.touched.html_body &&
                        validation.errors.html_body && (
                          <FormFeedback type="invalid">
                            {validation.errors.html_body}
                          </FormFeedback>
                        )}
                      <div className="form-text">
                        {t("templates.form.htmlHelp")}
                      </div>
                    </TabPane>
                    <TabPane tabId="preview">
                      <p className="text-muted small mb-2">
                        {t("templates.form.preview.hint")}
                      </p>
                      {validation.values.html_body.trim() ? (
                        <div
                          className="border rounded bg-white"
                          style={{ minHeight: 320 }}
                        >
                          <iframe
                            title={t("templates.form.tab.preview")}
                            sandbox=""
                            srcDoc={validation.values.html_body}
                            style={{
                              width: "100%",
                              minHeight: 320,
                              border: 0,
                            }}
                          />
                        </div>
                      ) : (
                        <p className="text-muted mb-0">
                          {t("templates.form.preview.emptyHtml")}
                        </p>
                      )}
                    </TabPane>
                  </TabContent>

                  <div className="d-flex flex-wrap gap-2 mt-4">
                    <Button color="primary" type="submit" disabled={saving}>
                      {saving ? (
                        <Spinner size="sm" />
                      ) : (
                        t("templates.form.save")
                      )}
                    </Button>
                    <Button
                      color="light"
                      type="button"
                      tag={Link}
                      to="/templates"
                      disabled={saving}
                    >
                      {t("templates.form.cancel")}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TemplateForm;
